import { RoseTree } from '@tableau/data-structures';
import { NestedProjectBasicInfo, NestedProjectsFractalRowStore, NestedProjectsTreeHelper } from '@tableau/nested-project-explorer';
import { Formatters, Messages } from '../../codegen/Localize';
import { ListResult } from '../fetchers/ListResult';
import { IActionContext } from '../helpers/ActionContext';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { L10n } from '../L10n/L10n';
import { DestinationProjectMapper } from '../mappers/DestinationProjectMapper';
import {
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  IContentType,
  MetricContentType,
  ProjectContentType,
  WorkbookContentType
} from '../models/ContentTypes';
import { FlowBaseInfo } from '../models/FlowInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataRoleBase } from '../models/IDataRoleInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IMetricInfo } from '../models/IMetricInfo';
import { IProjectBase } from '../models/IProjectInfo';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { IIdItemInfo, INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../resources/Datasources';
import { DestinationProjectContentType, DestinationProjects } from '../resources/DestinationProjects';
import { Flows } from '../resources/Flows';
import { Metrics } from '../resources/Metrics';
import { Projects } from '../resources/Projects';
import { Workbooks } from '../resources/Workbooks';
import { IDestinationProject } from '../ServerApi/types/IDestinationProject';
import { IProjectName } from '../ServerApi/types/IProject';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ContentActionNotification, IMultipleResultNotificationOptions, MultipleResultNotificationOptions } from './ContentActionNotification';
import { DataRoleErrors } from './DataRoleErrors';
import { DataSourceErrors } from './DataSourceErrors';
import { FlowErrors } from './FlowErrors';
import { MetricErrors } from './MetricErrors';
import { ModalService } from './ModalService';
import { IConfirmMoveToProjectScope, IResultWithDestinationProject } from './MoveToProjectAction';
import { ProjectErrors } from './ProjectErrors';
import { ServerService } from './ServerService';
import { WorkbookErrors } from './WorkbookErrors';

export const NoPermission = 'NO_PERMISSION';

export class MoveContentAction {
  public static $inject = [
    '$q',
    '$translate',
    'ContentActionNotification',
    'ConfirmActionDialog',
    'ServerService',
    'DestinationProjects',
    'Projects',
    'ProjectErrors',
    'DataSources',
    'DataSourceErrors',
    'Workbooks',
    'WorkbookErrors',
    'Flows',
    'FlowErrors',
    'DataRoles',
    'DataRoleErrors',
    'ModalService',
    'Metrics',
    'MetricErrors',
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private ContentActionNotification: ContentActionNotification,
    private ConfirmActionDialog: ConfirmActionDialog,
    private ServerService: ServerService,
    private DestinationProjects: DestinationProjects,
    private Projects: Projects,
    private ProjectErrors: ProjectErrors,
    private DataSources: DataSources,
    private DataSourceErrors: DataSourceErrors,
    private Workbooks: Workbooks,
    private WorkbookErrors: WorkbookErrors,
    private Flows: Flows,
    private FlowErrors: FlowErrors,
    private DataRoles: DataRoles,
    private DataRoleErrors: DataRoleErrors,
    private modalService: ModalService,
    private Metrics: Metrics,
    private MetricErrors: MetricErrors,
  ) {
  }

  public confirmMoveContent(content: IContentInfo[], actionContext: IActionContext): ng.IPromise<IResultWithDestinationProject> {

    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === ProjectContentType.name));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === WorkbookContentType.name));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === DatasourceContentType.name));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === FlowContentType.name));
    const dataRoles = Set.fromIdItemArray(content.filter(i => i.contentType === DataRoleContentType.name));
    const metrics = Set.fromIdItemArray(content.filter(i => i.contentType === MetricContentType.name));
    const count = content.length;
    const dialogScope: IConfirmMoveToProjectScope = actionContext.scope.$new() as IConfirmMoveToProjectScope;

    return this.initNestedProjectExplorer(dialogScope, projects, workbooks, datasources, flows, dataRoles, metrics).then((result) => {
      if (result === NoPermission) {
        return this.alertCantMoveContent(Messages.permissionsLongMessage_cannotMoveItems({ITEM_COUNT_LOC: Formatters.formatCount(content.length)})).result;
      }
      dialogScope.prompt = Messages.selectProjectForContent({ ITEM_COUNT: count, ITEM_NAME: L10n.elideIfLong(content[0].name), ITEM_COUNT_LOC: Formatters.formatCount(count) });

      const dialogInstance = this.ConfirmActionDialog.open({
        scope: dialogScope,
        title: Messages.moveContent(),
        bodyTemplateUrl: 'moveToProjectDialog.html',
        actionName: Messages.confirmMoveContent({ITEM_COUNT: count, ITEM_COUNT_LOC: Formatters.formatCount(count)}),
        action: () => this.moveContent(projects, workbooks, datasources, flows, dataRoles, metrics, dialogScope),
        confirmDisabled: () => dialogScope.projectExplorer.selectedProject == null
      });

      return dialogInstance.result
        .then((result: IResultWithDestinationProject[]) => ({ ...ResultHelper.combineResults(result), destinationProjectId: this.getDestinationProjectIdFromDialog(dialogScope)}));
    }).finally(() => dialogScope.$destroy());
  }

  private alertCantMoveContent(translatedMessage: string) {
    return this.modalService.alert({
      translatedMessage: translatedMessage,
      translatedTitle: Messages.permissions_cannotMoveItems()
    });
  }

  private getDestinationProjectIdFromDialog(dialogScope: IConfirmMoveToProjectScope): string {
    const destinationProjectId = dialogScope.projectExplorer.selectedProject.id;
    return (destinationProjectId === NestedProjectsFractalRowStore.SentinelSiteId ? null : destinationProjectId);
    // API expects a null ID if we're moving a project to the top level of the site
  }

  public initNestedProjectExplorer(
    scope: IConfirmMoveToProjectScope,
    projects: Set<IContentInfo>,
    workbooks: Set<IContentInfo>,
    datasources: Set<IContentInfo>,
    flows: Set<IContentInfo>,
    dataRoles: Set<IContentInfo>,
    metrics: Set<IContentInfo>,
  ): ng.IPromise<{}> {
    let self = this;
    scope.projectExplorer = {
      props: null,
      selectedProject: null
    };

    // The Nested Project Explorer presents the user a tree-view showing all the projects on the site,
    // along with their "status" (whether or not the items can actually be moved there).
    // We call the getDestinationProjects API to fetch the information to build this tree, however,
    // due to the limitations of the API, we split the source items by content type as well
    // as in groups of 1000 within each content type, and call the API separately for each of these chunks.
    //
    // The backend returns a complete list of Destination Projects for each chunk of source items
    // (let's call each copy a "slide"). Once we have all the slides, we combine them to create a
    // "projection" of all their statuses to show the user (the user must be able to move all the
    // items to a particular destination in order for it to be valid).
    let slidePromises = [];

    function pluckIds(items: Set<IIdItemInfo>) {
      return items.map(Set.PluckId);
    }

    if (projects.size() > 0) {
      slidePromises.push(...this.getDestinationProjectTreeSlidePromisesForSourceItems('', 'project', pluckIds(projects)));
    }
    if (workbooks.size() > 0) {
      slidePromises.push(...this.getDestinationProjectTreeSlidePromisesForSourceItems('', 'workbook', pluckIds(workbooks)));
    }
    if (datasources.size() > 0) {
      slidePromises.push(...this.getDestinationProjectTreeSlidePromisesForSourceItems('', 'datasource', pluckIds(datasources)));
    }
    if (this.ServerService.flowsOnSiteEnabled() && flows.size() > 0) {
      slidePromises.push(...this.getDestinationProjectTreeSlidePromisesForSourceItems('', 'flow', pluckIds(flows)));
    }
    if (dataRoles.size() > 0) {
      slidePromises.push(...this.getDestinationProjectTreeSlidePromisesForSourceItems('', 'dataRole', pluckIds(dataRoles)));
    }
    if (metrics.size() > 0) {
      slidePromises.push(...this.getDestinationProjectTreeSlidePromisesForSourceItems('', 'metric', pluckIds(metrics)));
    }

    return this.$q.all(slidePromises).then((slides: RoseTree<NestedProjectBasicInfo>[][]) => {

      // If all the slides are null, it means the user has no permission to move any of the content
      if (slides.every(x => x === null)) {
        return NoPermission;
      }

      // No scope.$apply here because it's called from within an ng.IPromise callback
      scope.projectExplorer.props = {
        siteName: self.ServerService.sessionSiteName(),
        topLevelDestinationStatus: self.getTopLevelDestinationStatus(datasources, flows, workbooks, dataRoles, metrics),
        projects: self.makeDestinationProjectSlideProjection(slides),
        onSelectedProjectChanged: (project: NestedProjectBasicInfo | null) =>
          scope.$apply(() => scope.projectExplorer.selectedProject = project)
      };
    });
  }

  public makeDestinationProjectSlideProjection(slides: RoseTree<NestedProjectBasicInfo>[][]) {
    let self = this;
    let projection: RoseTree<NestedProjectBasicInfo>[] = null;
    slides.forEach(function(slide) {
      if (slide === null) {
        return;
      } else if (projection === null) {
        projection = slide;
      } else {
        projection = self.addSlideToProjection(slide, projection);
      }
    });
    return projection;
  }

  private addSlideToProjection(slide: RoseTree<NestedProjectBasicInfo>[],
                               projection: RoseTree<NestedProjectBasicInfo>[]) {
    let self = this;
    slide.forEach(function(node, i) {
      if (node.node.hasChildren) {
        self.addSlideToProjection(node.children, projection[i].children);
      }

      // TODO: Should we have a precedence of the statuses here? Perhaps have a new status
      // that combines the messaging for the various statuses if they're different?
      if(node.node.status !== 'valid') {
        projection[i].node.status = node.node.status;
      }
    });
    return projection;
  }

  public getTopLevelDestinationStatus(datasources: Set<IContentInfo>, flows: Set<IContentInfo>, workbooks: Set<IContentInfo>, dataRoles: Set<IContentInfo>, metrics: Set<IContentInfo>) {
    if(workbooks.size() > 0 || datasources.size() > 0 || flows.size() > 0 || dataRoles.size() > 0 || metrics.size() > 0) {
      return 'structurally_invalid';
    } else if (!this.ServerService.isSessionSiteOrServerAdmin()) {
      return 'insufficient_permissions';
    } else {
      return 'valid';
    }
  }

  // Public for unit testing.
  public getDestinationProjectTreeSlidePromisesForSourceItems(
    query: string,
    contentType: DestinationProjectContentType,
    itemIds: string[],
  ): ng.IPromise<RoseTree<NestedProjectBasicInfo>[]>[] {

    // We can send only 1000 IDs to the API at a time, so make chunks of 1000 IDs and do the queries in parallel.
    const idBlocks = RequestHelper.makeBlocks(itemIds, RequestHelper.DefaultBlockSize);
    return idBlocks.map(idBlock => {
      const projectsQuery = this.DestinationProjects.getQueryForSpecificSourceContentAndMatchText(
          contentType, idBlock, query);

      return SliceableHelper.fetchAll(projectsQuery).then(MoveContentAction.createDestinationProjectTreeSlide);
    });
  }

  private static createDestinationProjectTreeSlide(projects: ListResult<IDestinationProject>): RoseTree<NestedProjectBasicInfo>[] {
    // Use the result of getDestinationProjects to infer if the user can move the items.
    // If no items are returned, the user doesn't have permission to move some of the selected items.
    // The check needs to be items[0] being undefined as the response that comes back is actually an array with first item as undefined.
    if (projects.items[0] === undefined) {
      return null;
    }

    const destinationProjects = DestinationProjectMapper.map(projects.items);
    return NestedProjectsTreeHelper.convertProjectListToTrees(destinationProjects);
  }

  private moveContent(projects: Set<IContentInfo>, workbooks: Set<IContentInfo>, datasources: Set<IContentInfo>,
                      flows: Set<IContentInfo>, dataRoles: Set<IContentInfo>, metrics: Set<IContentInfo>, dialogScope: IConfirmMoveToProjectScope) {
    const destinationProjectId = this.getDestinationProjectIdFromDialog(dialogScope);
    const moveFunctions = [
      this.moveProjects(projects, dialogScope, destinationProjectId),
      this.moveWorkbooks(workbooks, dialogScope, destinationProjectId),
      this.moveDatasources(datasources, dialogScope, destinationProjectId),
      this.moveDataRoles(dataRoles, dialogScope, destinationProjectId),
      this.moveMetrics(metrics, dialogScope, destinationProjectId),
    ];
    if (this.ServerService.flowsOnSiteEnabled()) {
      moveFunctions.push(this.moveFlows(flows, dialogScope, destinationProjectId));
    }

    return this.$q.all(moveFunctions);
  }

  private moveProjects(projects: Set<IContentInfo>, dialogScope: IConfirmMoveToProjectScope, destinationProjectId: string): ng.IPromise<IResult> {
    if (projects.size() < 1) {
      return this.$q.when(null);
    }
    return this.Projects.moveToProject(destinationProjectId, projects.map(project => project.id))
    .then((result: IResult) => {
      this.ContentActionNotification.notify(
        this.notification(
          new MultipleItemResult(projects, result),
          'moveProjectsToProjectSuccess',
          'moveProjectsToProjectError',
          ProjectContentType,
          dialogScope.projectExplorer.selectedProject
        )
      );
      return result;
    });
  }

  private moveMetrics(metrics: Set<IContentInfo>, dialogScope: IConfirmMoveToProjectScope, destinationProjectId: string): ng.IPromise<IResult> {
    if (metrics.size() < 1) {
      return this.$q.when(null);
    }
    return this.Metrics.moveToProject(destinationProjectId, metrics.map(metric => metric.id))
    .then((result: IResult) => {
      this.ContentActionNotification.notify(
        this.notification(
          new MultipleItemResult(metrics, result),
          'moveMetricsToProjectSuccess',
          'moveMetricsToProjectError',
          MetricContentType,
          dialogScope.projectExplorer.selectedProject
        )
      );
      return result;
    });
  }

  private moveWorkbooks(workbooks: Set<IContentInfo>, dialogScope: IConfirmMoveToProjectScope, destinationProjectId: string): ng.IPromise<IResult> {
    if (workbooks.size() < 1) {
      return this.$q.when(null);
    }
    return this.Workbooks.moveToProject(destinationProjectId, workbooks.map(workbook => workbook.id))
    .then((result: IResult) => {
      this.ContentActionNotification.notify(
        this.notification(
          new MultipleItemResult(workbooks, result),
          'moveWorkbooksToProjectSuccess',
          'moveWorkbooksToProjectError',
          WorkbookContentType,
          dialogScope.projectExplorer.selectedProject
        )
      );
      return result;
    });
  }

  private moveFlows(flows: Set<IContentInfo>, dialogScope: IConfirmMoveToProjectScope, destinationProjectId: string): ng.IPromise<IResult> {
    if (flows.size() < 1) {
      return this.$q.when(null);
    }
    return this.Flows.moveToProject(destinationProjectId, flows.map(flow => flow.id))
    .then((result: IResult) => {
      this.ContentActionNotification.notify(
        this.notification(
          new MultipleItemResult(flows, result),
          'moveFlowsToProjectSuccess',
          'moveFlowsToProjectError',
          FlowContentType,
          dialogScope.projectExplorer.selectedProject
        )
      );
      return result;
    });
  }

  private moveDataRoles(dataRoles: Set<IContentInfo>, dialogScope: IConfirmMoveToProjectScope, destinationProjectId: string): ng.IPromise<IResult> {
    if (dataRoles.size() < 1) {
      return this.$q.when(null);
    }
    return this.DataRoles.moveToProject(destinationProjectId, dataRoles.map(dr => dr.id))
      .then((result: IResult) => {
        this.ContentActionNotification.notify(
          this.notification(
            new MultipleItemResult(dataRoles, result),
            'moveDataRolesToProjectSuccess',
            'moveDataRolesToProjectError',
            DataRoleContentType,
            dialogScope.projectExplorer.selectedProject
          )
        );
        return result;
      });
  }

  private moveDatasources(datasources: Set<IContentInfo>, dialogScope: IConfirmMoveToProjectScope, destinationProjectId: string): ng.IPromise<IResult> {
    if (datasources.size() < 1) {
      return this.$q.when(null);
    }
    return this.DataSources.moveToProject(destinationProjectId, datasources.map(datasource => datasource.id))
    .then((result: IResult) => {
      this.ContentActionNotification.notify(
        this.notification(
          new MultipleItemResult(datasources, result),
          'moveDatasourcesToProjectSuccess',
          'moveDatasourcesToProjectError',
          DatasourceContentType,
          dialogScope.projectExplorer.selectedProject
        )
      );
      return result;
    });
  }

  private notification(
    result: MultipleItemResult<INamedItemInfo>,
    successId: string,
    errorId: string,
    itemType: IContentType,
    project: IProjectName): IMultipleResultNotificationOptions {

    return MultipleResultNotificationOptions.create(result,
      {
        describeSuccessSummary: (successCount: number) =>
          this.$translate.instant(successId, {
            ITEM_COUNT: successCount,
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }, L10n.Interpolations.Count),

        describeErrorSummary: (errorCount: number) =>
          this.$translate.instant(errorId, {
            ERROR_COUNT: errorCount,
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }, L10n.Interpolations.Count),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemType === ProjectContentType) {
            return this.ProjectErrors.errorDetailsFor(<IErrorItem<IProjectBase>>errorItem);
          } else if (itemType === DatasourceContentType) {
            return this.DataSourceErrors.errorDetailsFor(<IErrorItem<IDataSourceBase>>errorItem);
          } else if (itemType === WorkbookContentType) {
            return this.WorkbookErrors.errorDetailsFor(<IErrorItem<IWorkbookBase>>errorItem);
          } else if (itemType === FlowContentType) {
            return this.FlowErrors.errorDetailsFor(<IErrorItem<FlowBaseInfo>>errorItem);
          } else if (itemType === DataRoleContentType) {
            return this.DataRoleErrors.errorDetailsFor(<IErrorItem<IDataRoleBase>>errorItem);
          } else if (itemType === MetricContentType) {
            return this.MetricErrors.errorDetailsFor(<IErrorItem<IMetricInfo>>errorItem);
          }
        }
      }
    );
  }

}

Module.instance().service('MoveContentAction', MoveContentAction);
